/**
 * AuthProvider — owns the cached /me snapshot.
 *
 * Hook lives in `use-auth.ts`; the bare context lives in
 * `auth-context-value.ts`. The split is deliberate: the linter's
 * react-refresh rule wants component files to export only components.
 *
 * Two pieces of behaviour live here:
 *
 *  1. **Cached principal.** ``getMe()`` on mount; ``refresh()`` /
 *     ``logout()`` exposed via the context.
 *  2. **Auth-revoked sync.** A window listener for
 *     ``senti:auth-revoked`` (dispatched by the axios interceptor in
 *     ``api.ts`` on any 401) clears ``user`` so route guards see
 *     "unauthenticated" on the next render.
 *
 * Why (2) is here, not in App.tsx alongside the navigate listener:
 * without it, the navigate to ``/login`` lands on ``<RequirePublic>``
 * which reads the *cached* ``user`` from this provider, sees the
 * still-authenticated principal, and bounces back to ``/`` — leaving
 * the operator on a half-broken authenticated page issuing more 401s
 * (issue #141).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { AUTH_REVOKED_EVENT, getMe, postLogout, type MeResponse } from "./api";
import { AuthContext, type AuthContextValue } from "./auth-context-value";
import { queryClient } from "./query-client";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
      setError(null);
      return me;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        // Expected "not signed in" path. Clear the principal and let
        // route guards send the user to /login.
        setUser(null);
        setError(null);
        return null;
      }
      // Non-401 (network blip, 5xx, CORS): preserve the last-known-good
      // `user` snapshot so a transient outage doesn't unauthenticate a
      // working session. Pages can read `error` from the context to
      // render an inline alert with a retry button.
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await postLogout();
    } catch {
      // Logout is best-effort — even if the server reply fails (network
      // blip, already-revoked session) we wipe local state so the UI
      // can't claim the operator is still signed in.
    }
    setUser(null);
    setError(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // A 401 from any endpoint (not just /me) means the server-side
    // session is gone. Clear the cached principal so the next render
    // resolves route guards as "unauthenticated"; the navigate to
    // /login is owned by the sibling listener in App.tsx#RootShell.
    // Also drop the React Query cache so stale data doesn't flash
    // back into view if the operator re-authenticates in the same tab.
    function onAuthRevoked() {
      setUser(null);
      setError(null);
      queryClient.clear();
    }
    window.addEventListener(AUTH_REVOKED_EVENT, onAuthRevoked);
    return () => window.removeEventListener(AUTH_REVOKED_EVENT, onAuthRevoked);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, refresh, logout }),
    [user, loading, error, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
